import React, { useState, useEffect, useRef } from 'react';
import { Mic, MicOff, X, Volume2 } from 'lucide-react';

const VoiceMode = ({ isOpen, onClose, onSend, isDarkTheme, botResponse }) => {
    const [isListening, setIsListening] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [recognition, setRecognition] = useState(null);
    const [soundLevel, setSoundLevel] = useState(0);
    const [lastSpeechTime, setLastSpeechTime] = useState(Date.now());
    const [waitingForResponse, setWaitingForResponse] = useState(false);
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const microphoneRef = useRef(null);
    const speechTimeoutRef = useRef(null);
    const currentVoicesRef = useRef([]);
    const finalTranscriptRef = useRef('');
    // Use a ref to store the last spoken bot response to prevent repetition.
    const lastSpokenResponseRef = useRef('');

    // Initialize speech recognition and voices
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            if (SpeechRecognition) {
                const recognitionInstance = new SpeechRecognition();
                recognitionInstance.continuous = true;
                recognitionInstance.interimResults = true;
                recognitionInstance.lang = 'en-US';

                recognitionInstance.onresult = (event) => {
                    let finalText = '';
                    let interimText = '';

                    for (let i = event.resultIndex; i < event.results.length; ++i) {
                        if (event.results[i].isFinal) {
                            finalText += event.results[i][0].transcript;
                        } else {
                            interimText += event.results[i][0].transcript;
                        }
                    }

                    if (finalText) {
                        finalTranscriptRef.current = (finalTranscriptRef.current + ' ' + finalText).trim();
                        setTranscript(finalTranscriptRef.current);
                        setLastSpeechTime(Date.now());

                        // Clear any existing timeout since we have new speech
                        if (speechTimeoutRef.current) {
                            clearTimeout(speechTimeoutRef.current);
                        }

                        // Set a timeout to detect pause in speech (3 seconds)
                        speechTimeoutRef.current = setTimeout(() => {
                            if (finalTranscriptRef.current.trim() && !isSpeaking) {
                                handleSendMessage(finalTranscriptRef.current.trim());
                                finalTranscriptRef.current = '';
                                setTranscript('');
                            }
                        }, 3000);
                    } else if (interimText) {
                        setTranscript(finalTranscriptRef.current + ' ' + interimText);
                        setLastSpeechTime(Date.now());
                    }
                };

                setRecognition(recognitionInstance);
            }
        }

        const initVoices = () => {
            currentVoicesRef.current = window.speechSynthesis.getVoices();
        };

        if (window.speechSynthesis) {
            initVoices();
            window.speechSynthesis.onvoiceschanged = initVoices;
        }

        return () => {
            if (speechTimeoutRef.current) {
                clearTimeout(speechTimeoutRef.current);
            }
            if (window.speechSynthesis) {
                window.speechSynthesis.cancel(); // Cancel any ongoing speech on unmount
            }
        };
    }, []);

    // Monitor audio levels when listening
    useEffect(() => {
        if (isListening) {
            startAudioAnalysis();
        } else {
            stopAudioAnalysis();
        }
    }, [isListening]);

    // When the modal closes or reopens, reset transcript and last spoken response
    useEffect(() => {
        if (!isOpen) {
            finalTranscriptRef.current = '';
            setTranscript('');
            lastSpokenResponseRef.current = '';
            if (window.speechSynthesis) {
                window.speechSynthesis.cancel();
            }
        }
    }, [isOpen]);

    // Process bot response when it arrives
    useEffect(() => {
        const responseText = botResponse?.trim();
        // Only speak if there's a response, we're not speaking, the modal is open,
        // and this response hasn’t been spoken already.
        if (responseText && !isSpeaking && isOpen && lastSpokenResponseRef.current !== responseText) {
            lastSpokenResponseRef.current = responseText;
            setTimeout(() => {
                speakResponse(responseText);
            }, 300);
        }
    }, [botResponse, isSpeaking, isOpen]);

    const startAudioAnalysis = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            analyserRef.current = audioContextRef.current.createAnalyser();
            microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);

            microphoneRef.current.connect(analyserRef.current);
            analyserRef.current.fftSize = 32;

            const bufferLength = analyserRef.current.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);

            const analyzeSound = () => {
                if (!isListening) return;
                analyserRef.current.getByteFrequencyData(dataArray);
                const level = Math.max(...dataArray) / 255;
                setSoundLevel(level);
                requestAnimationFrame(analyzeSound);
            };

            analyzeSound();
        } catch (error) {
            console.error('Error accessing microphone:', error);
        }
    };

    const stopAudioAnalysis = () => {
        if (microphoneRef.current) {
            microphoneRef.current.disconnect();
            microphoneRef.current = null;
        }

        if (audioContextRef.current) {
            audioContextRef.current.close().catch(e => console.error('Error closing audio context:', e));
            audioContextRef.current = null;
        }

        setSoundLevel(0);
    };

    const handleSendMessage = (message) => {
        if (!isSpeaking && message.trim()) {
            // Prevent sending a message that exactly matches the bot's response.
            if (botResponse && message.trim() === botResponse.trim()) {
                console.log("Prevented sending bot response as user message");
                return;
            }
            console.log("Sending message:", message);
            onSend({ sender: 'user', message: message.trim() });
        }
    };

    const speakResponse = (text) => {
        if (!text || !window.speechSynthesis) return;

        // Clean the text and remove any leading colon
        let cleanedText = text
            .replace(/[*_~`#^<>{}[\]|\\]/g, '')
            .replace(/(\s+([.,!?]))/g, '$2')
            .replace(/,/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        // Remove any leading colon and whitespace if present
        cleanedText = cleanedText.replace(/^:\s*/, '');

        console.log("Speaking response:", cleanedText);
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(cleanedText);
        const voices = currentVoicesRef.current;
        const preferredVoices = voices.filter(v => v.lang.startsWith('en'));
        const defaultVoice = preferredVoices.find(v => v.default) || preferredVoices[0];

        if (defaultVoice) {
            utterance.voice = defaultVoice;
        }

        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utterance.text = cleanedText;

        utterance.onstart = () => {
            console.log("Speech started");
            setIsSpeaking(true);
            if (isListening) recognition?.stop();
        };

        utterance.onend = () => {
            console.log("Speech ended");
            setIsSpeaking(false);
            setTimeout(() => {
                if (isListening && !isSpeaking) {
                    recognition?.start();
                }
            }, 1000);
        };

        utterance.onerror = (e) => {
            console.error('Speech error:', e);
            setIsSpeaking(false);
            window.speechSynthesis.cancel();
        };

        try {
            window.speechSynthesis.speak(utterance);
        } catch (e) {
            console.error('Speech failed:', e);
            setIsSpeaking(false);
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (window.speechSynthesis) {
                window.speechSynthesis.cancel();
            }
        };
    }, []);

    useEffect(() => {
        if (!isSpeaking && !isListening && transcript) {
            // User has finished speaking, so we set waitingForResponse to true
            setWaitingForResponse(true);

            // Automatically clear it after a few seconds (adjust timing as needed)
            setTimeout(() => setWaitingForResponse(false), 3000);
        }
    }, [isSpeaking, isListening, transcript]);

    const toggleListening = () => {
        if (isSpeaking) return;

        if (isListening) {
            if (speechTimeoutRef.current) {
                clearTimeout(speechTimeoutRef.current);
            }
            recognition?.stop();
            stopAudioAnalysis();

            // Send any remaining transcript before stopping
            if (finalTranscriptRef.current.trim()) {
                handleSendMessage(finalTranscriptRef.current.trim());
                finalTranscriptRef.current = '';
                setTranscript('');
            }
        } else {
            // When starting, clear previous transcript and last spoken response
            finalTranscriptRef.current = '';
            setTranscript('');
            lastSpokenResponseRef.current = '';
            recognition?.start();
            startAudioAnalysis();
        }
        setIsListening(!isListening);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 flex flex-col items-center justify-center bg-black/90 backdrop-blur-md z-[400]">
            <button
                onClick={onClose}
                className="absolute top-6 right-6 p-2 rounded-full text-gray-400 hover:bg-gray-700"
                disabled={isSpeaking}
            >
                <X className="w-8 h-8" />
            </button>

            <div className="flex items-center justify-center mt-[-220px]">
                <div className={`w-72 h-72 rounded-full flex items-center justify-center bg-gradient-to-r ${isSpeaking ? 'from-blue-500 to-purple-500' : 'from-black to-amber-500'} animate-pulse relative`}>
                    {/* Sound Waves for listening */}
                    {isListening && soundLevel > 0.1 && !isSpeaking && (
                        <>
                            <div
                                className="absolute w-full h-full rounded-full border-2 border-amber-500/30 animate-wave-1"
                                style={{ transform: `scale(${1 + soundLevel * 0.5})` }}
                            ></div>
                            <div
                                className="absolute w-full h-full rounded-full border-2 border-amber-500/30 animate-wave-2"
                                style={{ transform: `scale(${1 + soundLevel * 0.5})` }}
                            ></div>
                            <div
                                className="absolute w-full h-full rounded-full border-2 border-amber-500/30 animate-wave-3"
                                style={{ transform: `scale(${1 + soundLevel * 0.5})` }}
                            ></div>
                        </>
                    )}

                    {/* Speaking animation */}
                    {isSpeaking && (
                        <>
                            <div className="absolute w-full h-full rounded-full border-2 border-blue-500/30 animate-wave-1"></div>
                            <div className="absolute w-full h-full rounded-full border-2 border-purple-500/30 animate-wave-2"></div>
                            <div className="absolute w-full h-full rounded-full border-2 border-blue-500/30 animate-wave-3"></div>
                        </>
                    )}

                    <div className="w-64 h-64 rounded-full flex items-center justify-center backdrop-blur-md bg-white/10 animate-spin-slow">
                        <div className="w-56 h-56 rounded-full flex items-center justify-center backdrop-blur-md bg-white/10 animate-pulse">
                            <div className="w-48 h-48 rounded-full flex items-center justify-center backdrop-blur-md bg-white/10 animate-pulse">
                                <div className="w-40 h-40 rounded-full flex items-center justify-center backdrop-blur-md bg-white/10 animate-pulse">
                                    <div className="w-32 h-32 rounded-full flex items-center justify-center backdrop-blur-md bg-white/10 animate-pulse"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Transcript display (optional) */}
            {transcript && !isSpeaking && (
                <div className="absolute top-1/3 w-full max-w-md px-6 mt-6">
                    <div className="bg-gray-800/80 text-white p-3 rounded-lg">
                        {transcript}
                    </div>
                </div>
            )}

            <div className="absolute bottom-10 flex w-full max-w-md px-6 justify-between items-center">
                <button
                    onClick={toggleListening}
                    className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all
                        ${isSpeaking ? 'bg-gray-500 cursor-not-allowed opacity-50' :
                            isListening ? 'bg-red-500 hover:bg-red-600' : 'bg-amber-500 hover:bg-amber-600'}
                        text-white font-medium`}
                    disabled={isSpeaking}
                >
                    {isListening ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                    <span>{isListening ? 'Stop' : 'Start'}</span>
                </button>
                <div className="flex items-center gap-2 text-gray-300 mt-3">
                    <Volume2 className={`w-6 h-6 ${isSpeaking ? 'text-blue-400 animate-pulse' : ''}`} />
                    <span className="text-lg">{isSpeaking ? 'Speaking...' : 'Ready'}</span>
                </div>
            </div>

            {isSpeaking && (
                <div className="absolute bottom-28 text-center text-white bg-black/50 px-4 py-2 rounded-lg">
                    Please wait while I'm speaking...
                </div>
            )}

            {!isSpeaking && isListening && transcript && (
                <div className="absolute bottom-28 text-center text-white bg-black/50 px-4 py-2 rounded-lg">
                    Listening... Query will be sent after 3 seconds of silence
                </div>
            )}

            {!isSpeaking && !isListening && waitingForResponse && (
                <div className="absolute bottom-28 text-center text-white bg-black/50 px-4 py-2 rounded-lg">
                    Responding in few seconds...
                </div>
            )}
        </div>
    );
};

export default VoiceMode;
